import { useEffect, useMemo, useState } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  LayoutGrid,
  Plus,
  Trash2,
  TriangleAlert,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  EmptyState,
  ErrorState,
  Page,
  PageBody,
  PageHeader,
  Panel,
  Skeleton,
} from '@/ui/Panel';
import { Button } from '@/ui/Button';
import { SearchInput } from '@/ui/Field';
import { Badge, Code } from '@/ui/Badge';
import { Callout } from '@/ui/Callout';
import { useDebouncedValue } from '@/ui/MultiSelect';
import { api, errorMessage } from '@/lib/api/client';
import { countLabel, money } from '@/lib/format';
import type {
  ProductDetail,
  ProductListResponse,
  SettingsResponse,
} from '@/lib/api/types';

/**
 * The register's quick panel.
 *
 * Stored as one setting — `quick_panel_items`, a JSON array of product ids —
 * and read back by the register's `get_quick_panel_items` command, which
 * returns the products *in the stored order*. So order is data here, not
 * decoration, and the list is edited as an ordered sequence.
 *
 * Ids whose product no longer resolves are shown rather than silently dropped:
 * a stale id is a real defect on the lane (a blank button), and the operator
 * should see it and remove it deliberately.
 */
export const Route = createFileRoute('/_app/settings_/quick-panel')({
  staticData: { crumb: 'Quick panel' },
  component: QuickPanelScreen,
});

const SETTING_KEY = 'quick_panel_items';

function parseIds(raw: string | null | undefined): number[] {
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (v): v is number => typeof v === 'number' && Number.isInteger(v) && v > 0
    );
  } catch {
    return [];
  }
}

function QuickPanelScreen() {
  const queryClient = useQueryClient();

  const settings = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.get<SettingsResponse>('/settings'),
  });

  const savedIds = useMemo(() => parseIds(settings.data?.[SETTING_KEY]), [settings.data]);

  const [ids, setIds] = useState<number[] | null>(null);
  useEffect(() => {
    if (settings.data && ids === null) setIds(savedIds);
  }, [settings.data, savedIds, ids]);

  const working = ids ?? savedIds;
  const dirty =
    working.length !== savedIds.length || working.some((id, i) => id !== savedIds[i]);

  // One cached query per product id — shared with every other screen that has
  // already looked the product up, and individually retried.
  const products = useQueries({
    queries: working.map((id) => ({
      queryKey: ['products', String(id)],
      queryFn: () => api.get<ProductDetail>(`/products/${id}`),
      staleTime: 5 * 60_000,
      retry: false,
    })),
  });

  const save = useMutation({
    mutationFn: (next: number[]) =>
      api.put<SettingsResponse>('/settings', { [SETTING_KEY]: JSON.stringify(next) }),
    onSuccess: async (_result, next) => {
      await queryClient.invalidateQueries({ queryKey: ['settings'] });
      setIds(next);
      toast.success('Quick panel saved', {
        description: `${countLabel(next.length, 'item')}. Registers pick it up on their next sync.`,
      });
    },
    onError: (error) => toast.error('Save failed', { description: errorMessage(error) }),
  });

  function move(index: number, delta: number) {
    const next = [...working];
    const target = index + delta;
    const a = next[index];
    const b = next[target];
    if (a === undefined || b === undefined) return;
    next[index] = b;
    next[target] = a;
    setIds(next);
  }

  function remove(id: number) {
    setIds(working.filter((v) => v !== id));
  }

  function add(id: number) {
    if (working.includes(id)) {
      toast.info('Already on the panel');
      return;
    }
    setIds([...working, id]);
  }

  if (settings.isError) {
    return (
      <Page>
        <PageHeader eyebrow="Settings" title="Quick panel" />
        <PageBody>
          <ErrorState
            message={errorMessage(settings.error)}
            onRetry={() => void settings.refetch()}
          />
        </PageBody>
      </Page>
    );
  }

  const unresolved = working.filter((_, i) => products[i]?.isError);

  return (
    <Page>
      <PageHeader
        eyebrow={
          <Link to="/settings" className="hover:text-ink hover:underline">
            Settings
          </Link>
        }
        title="Quick panel"
        subtitle="Shortcut products on the register's quick-access board, in the order they appear."
        actions={
          <>
            <Button
              size="sm"
              variant="ghost"
              icon={<ArrowLeft className="size-3.5" />}
              asChild
            >
              <Link to="/settings">Settings</Link>
            </Button>
            {dirty ? (
              <>
                <Button size="sm" variant="ghost" onClick={() => setIds(savedIds)}>
                  Discard
                </Button>
                <Button
                  size="sm"
                  variant="primary"
                  loading={save.isPending}
                  onClick={() => save.mutate(working)}
                >
                  Save panel
                </Button>
              </>
            ) : null}
          </>
        }
      />

      <PageBody className="space-y-4">
        {unresolved.length > 0 ? (
          <Callout
            tone="warn"
            icon={<TriangleAlert />}
            title={`${countLabel(unresolved.length, 'item')} no longer resolve`}
          >
            These ids are still in the setting but the product could not be loaded — most
            likely it was deleted. They render as blank buttons on the lane. Remove them
            and save.
          </Callout>
        ) : null}

        <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_24rem]">
          <Panel
            title="On the panel"
            description={countLabel(working.length, 'item')}
            bleed
          >
            {settings.isLoading ? (
              <div className="space-y-2 p-3">
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
              </div>
            ) : working.length === 0 ? (
              <EmptyState
                icon={<LayoutGrid />}
                title="No quick panel items"
                description="Search on the right and add the products cashiers reach for most."
              />
            ) : (
              <ol className="divide-line-soft divide-y">
                {working.map((id, index) => {
                  const result = products[index];
                  const product = result?.data;
                  const failed = result?.isError ?? false;
                  return (
                    <li key={id} className="flex items-center gap-3 px-3 py-2">
                      <span className="text-2xs text-ink-faint w-6 shrink-0 text-right font-mono tabular-nums">
                        {index + 1}
                      </span>
                      <span className="min-w-0 flex-1">
                        {result?.isLoading ? (
                          <Skeleton className="h-3 w-40" />
                        ) : failed ? (
                          <span className="flex items-center gap-2">
                            <span className="text-negative text-sm">
                              Unresolved product
                            </span>
                            <Badge tone="negative">stale id</Badge>
                          </span>
                        ) : (
                          <span className="text-ink block truncate text-sm">
                            {product?.name ?? '—'}
                          </span>
                        )}
                        <span className="mt-0.5 flex items-center gap-1.5">
                          <Code>#{id}</Code>
                          {product?.sku ? <Code>{product.sku}</Code> : null}
                          {product ? (
                            <span className="text-2xs text-ink-faint font-mono">
                              {money(product.price)}
                            </span>
                          ) : null}
                        </span>
                      </span>
                      <span className="flex shrink-0 items-center gap-0.5">
                        <Button
                          size="icon-sm"
                          variant="ghost"
                          aria-label="Move up"
                          disabled={index === 0}
                          onClick={() => move(index, -1)}
                        >
                          <ChevronUp className="size-3.5" />
                        </Button>
                        <Button
                          size="icon-sm"
                          variant="ghost"
                          aria-label="Move down"
                          disabled={index === working.length - 1}
                          onClick={() => move(index, 1)}
                        >
                          <ChevronDown className="size-3.5" />
                        </Button>
                        <Button
                          size="icon-sm"
                          variant="ghost"
                          aria-label="Remove"
                          onClick={() => remove(id)}
                        >
                          <Trash2 className="size-3.5" />
                        </Button>
                      </span>
                    </li>
                  );
                })}
              </ol>
            )}
          </Panel>

          <ProductPicker onAdd={add} chosen={working} />
        </div>
      </PageBody>
    </Page>
  );
}

/* ── Picker ───────────────────────────────────────────────────────────────── */

function ProductPicker({
  onAdd,
  chosen,
}: {
  onAdd: (id: number) => void;
  chosen: number[];
}) {
  const [term, setTerm] = useState('');
  const search = useDebouncedValue(term);

  const results = useQuery({
    queryKey: ['products', { search, pageSize: 20, quickPanel: true }],
    queryFn: () =>
      api.get<ProductListResponse>('/products', {
        params: { search, page: 1, pageSize: 20, active: true },
      }),
    enabled: search.trim().length > 1,
  });

  return (
    <Panel title="Add a product" description="Search the catalog." bleed>
      <div className="border-line border-b p-2">
        <SearchInput
          size="sm"
          value={term}
          onValueChange={setTerm}
          placeholder="Name, SKU or barcode…"
        />
      </div>
      {search.trim().length <= 1 ? (
        <p className="text-2xs text-ink-faint p-3">
          Type at least two characters to search.
        </p>
      ) : results.isLoading ? (
        <div className="space-y-2 p-3">
          <Skeleton className="h-3 w-3/4" />
          <Skeleton className="h-3 w-2/3" />
        </div>
      ) : results.isError ? (
        <ErrorState
          className="m-3"
          message={errorMessage(results.error)}
          onRetry={() => void results.refetch()}
        />
      ) : (results.data?.data.length ?? 0) === 0 ? (
        <p className="text-2xs text-ink-faint p-3">No products match “{search}”.</p>
      ) : (
        <ul className="divide-line-soft divide-y">
          {(results.data?.data ?? []).map((product) => {
            const id = Number(product.id);
            const already = chosen.includes(id);
            return (
              <li key={product.id} className="flex items-center gap-2 px-3 py-2">
                <span className="min-w-0 flex-1">
                  <span className="text-ink block truncate text-xs">{product.name}</span>
                  <span className="flex items-center gap-1.5">
                    <Code>#{id}</Code>
                    <span className="text-2xs text-ink-faint font-mono">
                      {money(product.price)}
                    </span>
                  </span>
                </span>
                {already ? (
                  <Badge tone="neutral">on panel</Badge>
                ) : (
                  <Button
                    size="xs"
                    icon={<Plus className="size-3" />}
                    onClick={() => onAdd(id)}
                  >
                    Add
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
}
